import { Select, Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'

import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import ParameterMapping, {
    ExtendedParameterName,
} from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import RoutePicker, {
    RouteErrors,
    RouteProps,
} from 'views/CalculateEmissions/RFQ/components/RoutePicker'
import { resetAutoFill } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import {
    DistanceUnit,
    PropertyMapping,
    RouteMapping,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    LegAutoFillWithUpdate,
    PARAMETER_MAPPING_OUTPUT,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'

export type RouteType = 'Distance' | 'Origin and Destination'

export type RouteMethodProps = {
    state: RFQTemplateState
    routeType: RouteType | undefined
    originRouteMapping: RouteMapping | undefined
    destinationRouteMapping: RouteMapping | undefined
    routeTypeOnUpdate: (val: RouteType | undefined) => void
    originOnUpdate: RouteProps['onUpdate']
    destinationOnUpdate: RouteProps['onUpdate']
    originAutoFill: LegAutoFillWithUpdate['origin']
    destinationAutoFill: LegAutoFillWithUpdate['origin']
    routeTypeError?: string
    originError?: RouteErrors
    destinationError?: RouteErrors
}

// eslint-disable-next-line complexity
const RouteMethodPicker = ({
    state,
    routeType,
    originRouteMapping,
    destinationRouteMapping,
    routeTypeOnUpdate,
    originOnUpdate,
    destinationOnUpdate,
    originAutoFill,
    destinationAutoFill,
    routeTypeError,
    originError,
    destinationError,
}: RouteMethodProps) => {
    const handleRouteMethodChange = (val: RouteType | undefined) => {
        routeTypeOnUpdate(val)

        // TODO add distance to inference and populate if val === 'Distance'
        resetAutoFill(originAutoFill)
        resetAutoFill(destinationAutoFill)
        originOnUpdate(undefined, undefined)
        destinationOnUpdate(undefined, undefined)
    }

    const handleDistanceChange = (
        _parameterName: ExtendedParameterName,
        data: PropertyMapping<DistanceUnit> | undefined,
    ) => {
        // Propagate distance to origin and destination since both are required
        originOnUpdate({ type: 'Distance', distance: data }, undefined)
        destinationOnUpdate({ type: 'Distance', distance: data }, undefined)
    }

    return (
        <Box>
            <Text variant={'h6'} sx={{ mb: 4 }}>
                Route
            </Text>
            <Select
                key={routeType}
                error={routeTypeError}
                label={'Select a route type'}
                onChange={handleRouteMethodChange}
                value={routeType}
                items={[
                    { label: 'Distance', value: 'Distance' },
                    { label: 'Origin and Destination', value: 'Origin and Destination' },
                ]}
                sx={{ width: '45%' }}
                labelSx={{ textTransform: 'unset' }}
            />

            <Box marginTop="72px">
                {routeType === 'Distance' && (
                    <>
                        <Labels />
                        <ParameterMapping<DistanceUnit>
                            error={originError?.distance}
                            state={state}
                            parameter="distance"
                            mappingType="unit"
                            outputValues={PARAMETER_MAPPING_OUTPUT.distance}
                            formatOutput={String}
                            propertyMapping={
                                originRouteMapping && originRouteMapping.type === 'Distance'
                                    ? originRouteMapping.distance
                                    : undefined
                            }
                            onUpdate={handleDistanceChange}
                            autoFill={originAutoFill.Distance.distance}
                        />
                    </>
                )}

                {routeType && routeType !== 'Distance' && (
                    <>
                        <RoutePicker
                            errors={originError}
                            label="Origin"
                            state={state}
                            routeMapping={originRouteMapping}
                            onUpdate={originOnUpdate}
                            autoFill={originAutoFill}
                        />
                        <Box marginTop="72px"></Box>
                        <RoutePicker
                            errors={destinationError}
                            label="Destination"
                            state={state}
                            routeMapping={destinationRouteMapping}
                            onUpdate={destinationOnUpdate}
                            autoFill={destinationAutoFill}
                        />
                    </>
                )}
            </Box>
        </Box>
    )
}

export default RouteMethodPicker
